import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import pallet from '../../../layouts/pallet';
import { RowErrorGroup } from '../types';
import { dataGridSx } from '../../common/styled/DataGrid';
import CustomDataGridToolbar from '../../common/components/CustomDataGridToolbar';

interface ErrorTableProps {
  rows: RowErrorGroup[];
}

const ErrorTable: React.FC<ErrorTableProps> = ({ rows }) => {
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'rowNumber',
        headerName: 'CSV Row No',
        width: 90,
        type: 'number'
      },
      {
        field: 'studentEmail',
        headerName: 'Student Email',
        width: 240,
        sortable: false,
        renderCell: (params) => (
          <Box sx={{ fontSize: 12, lineHeight: 1.3, whiteSpace: 'normal' }}>
            {params.value}
          </Box>
        )
      },
      {
        field: 'errorCount',
        headerName: 'Error Count',
        width: 130,
        type: 'number'
      },
      {
        field: 'fieldKeys',
        headerName: 'Fields',
        flex: 1,
        minWidth: 220,
        sortable: false,
        cellClassName: 'multiline',
        renderCell: (params) => {
          const text = (params.value as string) || '';
          return (
            <Box sx={{ whiteSpace: 'pre-wrap', fontSize: 12, lineHeight: 1.3 }}>
              {text}
            </Box>
          );
        }
      },
      {
        field: 'messages',
        headerName: 'Details',
        flex: 2,
        minWidth: 400,
        sortable: false,
        cellClassName: 'multiline',
        renderCell: (params) => {
          const text = params.value as string;
          return (
            <Box sx={{ whiteSpace: 'pre-wrap', fontSize: 12, lineHeight: 1.3 }}>
              {text}
            </Box>
          );
        }
      }
    ],
    []
  );

  const rowHeight = useMemo(() => {
    if (!rows.length) return 52;
    const perLineCharCapacity = 100; // rough chars that fit before wrapping
    const lineHeightPx = 16; // fontSize 12 * lineHeight ~1.3
    const verticalPadding = 24; // cell padding + top/bottom margin buffer

    const maxExpandedLines = rows.reduce((max, r) => {
      const messages = r.messages || '';
      const logicalLines = messages.split('\n');
      let expanded = 0;
      logicalLines.forEach((line) => {
        expanded += Math.max(1, Math.ceil(line.length / perLineCharCapacity));
      });
      return Math.max(max, expanded);
    }, 1);

    const calculated = verticalPadding + maxExpandedLines * lineHeightPx;
    return Math.min(1000, Math.max(52, calculated));
  }, [rows]);

  return (
    <Box sx={{ width: '100%' }}>
      <DataGrid
        rows={rows.map((r) => ({ id: r.rowNumber, ...r }))}
        columns={columns}
        disableRowSelectionOnClick
        hideFooter
        disableColumnMenu
        showToolbar
        slots={{ toolbar: CustomDataGridToolbar }}
        rowHeight={rowHeight}
        sx={{
          ...dataGridSx(pallet),
          height: 420,
          maxHeight: 420,
          '& .MuiDataGrid-virtualScroller': {
            overflowY: 'auto'
          },
          '& .MuiDataGrid-cell.multiline .MuiDataGrid-cellContent': {
            whiteSpace: 'normal',
            overflow: 'visible',
            textOverflow: 'unset',
            lineHeight: 1.3,
            display: 'block'
          }
        }}
      />
    </Box>
  );
};

export default ErrorTable;
